import { IUserStore } from '@stores/user';
import { inject, observer } from 'mobx-react';
import React, { PureComponent, createContext, useContext } from 'react';
import _ from 'lodash';
import removeNullValues from '@utils/removeNullValues';
import { splitNotPrecededBy } from '@lib/utils/setPathToObject';
import {
  ErrorProps,
  fetchErrorAssets,
} from '@components/sources/visualizer/mappingScreens/errorsView';
import { FixMe } from '@utils/types';
import {
  generatePreview,
  getRespData,
  getApiErrorMessageDetails,
} from '@components/sources/visualizer/mappingScreens/sourceUtil';
import { SourceDataStore } from '@stores/sourceDataStore';
import { IColumn } from '../sourceSpecificConfig';
import { IMessageStore } from '@stores/messages';
import { FilterSpecProps } from '@stores/audiences/types';

export const overrides = ['message_id', 'anonymous_id', 'user_id', 'event', 'email'];
const maxColumnLength = 255;

const validateColumnName = (name: string) => {
  const currentValueSplit = splitNotPrecededBy(name, '\\.', '\\\\');
  const validateParts = currentValueSplit.every((n) =>
    /^[A-Z_a-z](?:\w*(?:\\(?=\.)\.)?\w{1,2})*(?:|\[\d])?$/.test(n),
  );
  const valid = /^[\d.A-Z\\a-z]*(?:\[\d])?[\w.\\]*\w$/.test(name);

  return validateParts && valid;
};

const validateNestedJsonName = (allKeys: string[], value: string) => {
  const currentValueSplit = splitNotPrecededBy(value.replace(/^traits\./, ''), '\\.', '\\\\').map(
    (v) => v.toLowerCase(),
  );

  const validatePathDeep = allKeys.every((rcol) => {
    if (/[^\\]\./.test(rcol)) {
      const paths = getPathList(rcol).map((v) => v.replace(/\[\d]/, '').toLowerCase());
      const currentPath = getPathList(value).map((v) => v.replace(/\[\d]/, '').toLowerCase());

      return paths.some((rpath, i) => currentPath[i] && rpath !== currentPath[i]);
    }
    return true;
  });
  return validatePathDeep && !allKeys.includes(currentValueSplit[0].replace(/\[\d]/, ''));
};
const validateDuplicates = (allNames: string[], value: string) =>
  !allNames.map((item) => item.toLowerCase()).includes(value.toLowerCase());
const getPathList = (path: string) => {
  const pathList = path.split('.');
  return pathList.reduce((accum: string[], val, i) => {
    if (i === 0) {
      accum[i] = val;
    } else {
      accum[i] = `${accum[i - 1]}.${val}`;
    }
    return accum;
  }, []);
};
export const important_overrides = ['anonymous_id', 'user_id'];
interface StoreProps {
  userStore?: IUserStore;
  sourceDataStore?: SourceDataStore;
  messagesStore?: IMessageStore;
}
export interface TableMappingProviderProps extends StoreProps {
  isValid: boolean;
  sourceConfig: ISourceInfo;
  selectedColumns?: string[];
  renamedRows?: { [i: string]: string[] };
  onChange?: (
    mapping: { original: string; mappedArray: string[] }[],
    selectedColumns: string[],
    overridesConfig?: { [i: string]: string },
    constantConfig?: { key: string; value: string }[],
  ) => void;
  columns?: string[];
  role: string;
  overridesConfig?: { [i: string]: string };
  constantsConfig?: { key: string; value: FixMe }[];
  eventType?: 'identify' | 'track';
  viewOnly: boolean;
  ifUpdate: boolean;
  setColumns: (cols: string[]) => void;
  updateSchemaErrors: (errorItems: ErrorProps[]) => void;
}

export interface ISourceInfo {
  accountId?: string;
  table?: string;
  schema?: string;
  bucketName?: string;
  prefix?: string;
  sqlModelId?: string;
  sql?: string;
  filterSpec?: FilterSpecProps;
}

interface State {
  viewOnly: boolean;
  ifUpdate: boolean;
  loading: boolean;
  selectedColumns: string[];
  searchKeyword?: string;
  editingRow?: string | null | undefined;
  renamedRows: { [i: string]: string[] };
  tableData?: { [i: string]: FixMe };
  jsonData?: { [i: string]: FixMe }[];
  overridesConfig?: { [i: string]: string };
  constantsConfig: { key: string; value: FixMe }[];
  sourceConfig?: ISourceInfo;
  role: string;
}

export interface MappingState extends State {
  setSelectedRow: (selectedColumns: string[] | string) => void;
  setEditingRow: (editingRow: string) => void;
  setSearchKeyword: (keyword: string) => void;
  setRenamedRows: (renamedRow: { [i: string]: string[] } | null) => void;
  setOverridesConfig: (overrideRow: { [i: string]: string } | null) => void;
  deleteFromRenamedRow: (renamedRow: { [i: string]: string[] } | null) => void;
  setConstantConfig: (constants: { key: string; value: FixMe }[]) => void;
  deleteConstantConfig: (key: string) => void;
  validateName: (newName: string, originalName: string | null, newNames: string[]) => string | null;
  refreshColumns: (columns: IColumn[]) => void;
  refreshPreview: (rows: Record<string, unknown>[]) => void;
  updateSchemaErrors: (errorItems: ErrorProps[]) => void;
}
const MappingContext = createContext<MappingState>({
  tableData: {},
  jsonData: [],
  constantsConfig: [],
  setConstantConfig: () => null,
  deleteConstantConfig: () => null,
  setSelectedRow: () => null,
  setEditingRow: () => null,
  setRenamedRows: () => null,
  deleteFromRenamedRow: () => null,
  setSearchKeyword: () => null,
  setOverridesConfig: () => null,
  selectedColumns: [],
  editingRow: null,
  loading: false,
  renamedRows: {},
  overridesConfig: {},
  viewOnly: false,
  ifUpdate: false,
  sourceConfig: { accountId: '' },
  role: '',
  validateName: () => 'err',
  refreshColumns: () => null,
  refreshPreview: () => null,
  updateSchemaErrors: () => null,
});

export const useMappingState = () => useContext(MappingContext);

@inject('userStore', 'sourceDataStore', 'messagesStore')
@observer
class MappingTableProvider extends PureComponent<TableMappingProviderProps, State> {
  constructor(props: TableMappingProviderProps) {
    super(props);
    this.state = {
      loading: false,
      selectedColumns: this.props.selectedColumns || [],
      renamedRows: this.props.renamedRows || {},
      overridesConfig: this.props.overridesConfig || {},
      constantsConfig: this.props.constantsConfig || [],
      sourceConfig: this.props.sourceConfig,
      viewOnly: this.props.viewOnly,
      ifUpdate: this.props.ifUpdate,
      role: this.props.role,
    };
  }

  setEditingRow = (editingRow: string) => {
    this.setState({
      editingRow,
    });
  };

  setConstantConfig = (constants: { key: string; value: FixMe }[]) => {
    this.setState(
      {
        constantsConfig: constants,
      },
      this.handleChange,
    );
  };

  deleteConstantConfig = (key: string) => {
    this.setState({
      constantsConfig: this.state.constantsConfig.filter((entry) => entry.key !== key),
    });
  };

  validateName = (newName: string, originalName: string | null, newNames: string[]) => {
    const { renamedRows, constantsConfig } = this.state;
    if (important_overrides.includes(newName.toLowerCase())) {
      return 'This value is reserved';
    }
    let restRenames = renamedRows;
    if (originalName) {
      const { [originalName]: defaultRenames, ...restRenamedValues } = renamedRows;
      restRenames = restRenamedValues;
    }
    let allNames = Object.values(restRenames).reduce(
      (accum, val) => [...accum, ...(val || [])],
      [...(newNames || [])],
    );

    allNames = [
      ...allNames,
      ...constantsConfig.map(({ key }: { key: string; value: string }) => key),
    ];

    const validateColDuplicates =
      validateDuplicates(allNames, newName) && validateNestedJsonName(allNames, newName);
    const isValidName = validateColumnName(newName);
    if (!validateColDuplicates) {
      return 'This column name already exists.';
    }
    if (!isValidName) {
      return 'This column name is not valid.';
    }
    if (newName?.length > maxColumnLength) {
      return `The max length of a column name should be below ${maxColumnLength} chars.`;
    }
    return null;
  };

  setSearchKeyword = (keyword: string) => {
    this.setState({
      searchKeyword: keyword,
    });
  };

  handleChange = () => {
    const { onChange } = this.props;
    if (onChange) {
      const { selectedColumns, renamedRows, overridesConfig = {}, constantsConfig } = this.state;
      const mapping = Object.keys(renamedRows).reduce(
        (accum: { original: string; mappedArray: string[] }[], column) => {
          const rename = renamedRows[column];
          accum.push({
            original: column,
            mappedArray: rename,
          });
          return accum;
        },
        [],
      );
      onChange(mapping, selectedColumns, overridesConfig, constantsConfig);
    }
  };

  setSelectedRow = (selectedRow: string[] | string) => {
    let newselectedColumns = selectedRow as string[];
    if (typeof selectedRow === 'string') {
      if (this.state.selectedColumns.includes(selectedRow)) {
        newselectedColumns = this.state.selectedColumns.filter((val) => val !== selectedRow);
      } else {
        newselectedColumns = [...this.state.selectedColumns, selectedRow];
      }
    }
    this.setState(
      {
        selectedColumns: newselectedColumns,
      },
      this.handleChange,
    );
  };

  setOverridesConfig = (overrideRow: { [i: string]: string } | null) => {
    const newOverridesConfig = overrideRow ? { ...this.state.overridesConfig, ...overrideRow } : {};

    const removeNullOverrides = Object.entries(newOverridesConfig || []).reduce(
      (accum: { [i: string]: string }, [k, v]: [string, string]) => {
        if (v) {
          accum[k] = v;
        }
        return accum;
      },
      {},
    );

    this.setState(
      {
        overridesConfig: removeNullOverrides,
      },
      this.handleChange,
    );
  };

  setRenamedRows = (renamedRow: { [i: string]: string[] } | null) => {
    if (!renamedRow) {
      const oldRenamedRows = this.getInitialRenamedRows(this.props.columns);
      this.initializeRenamedRows(oldRenamedRows || {});
      this.setSelectedRow(this.props.selectedColumns || []);
      return;
    }
    const renamedRowsAll = this.state.renamedRows ? this.state.renamedRows : null;
    Object.keys(renamedRow || []).forEach((key) => {
      if (renamedRow && renamedRowsAll && renamedRowsAll[key]) {
        renamedRow[key] = Array.from(new Set(renamedRow[key].concat(renamedRowsAll[key]))).map(
          (k) => (k.toLowerCase() === 'email' ? 'email' : k),
        );
      }
    });
    const newRenames = renamedRow ? { ...this.state.renamedRows, ...renamedRow } : {};
    this.setState(
      {
        renamedRows: removeNullValues(newRenames),
      },
      this.handleChange,
    );
  };

  initializeRenamedRows = async (renamedRow: { [i: string]: string[] }) => {
    this.setState(
      {
        renamedRows: renamedRow,
      },

      this.handleChange,
    );
  };

  getInitialOverrideConfig = (columns?: string[]) => {
    const oldoverridesConfig = columns?.reduce((accum: FixMe, col) => {
      if (important_overrides?.includes(col.toLowerCase())) {
        accum[col.toLowerCase()] = col;
      }
      return accum;
    }, {});
    return oldoverridesConfig;
  };

  getInitialRenamedRows = (columns?: string[]) => {
    const oldRenamedRows = columns?.reduce((accum: FixMe, col) => {
      if (important_overrides.includes(col.replace(/\./g, '.').toLowerCase())) {
        accum[col] = [];
      } else {
        const coll = col.replace(/\./g, '\\.');
        let collrenameValue = coll;
        if (col.toLowerCase() === 'email') {
          collrenameValue = 'email';
        }
        accum[coll] = [collrenameValue];
      }
      return accum;
    }, {});
    return oldRenamedRows || [];
  };

  deleteFromRenamedRow = (renamedRow: { [i: string]: string[] } | null) => {
    const renamedRowsAll = this.state.renamedRows ? this.state.renamedRows : null;
    Object.keys(renamedRow || []).forEach((key) => {
      if (!renamedRow || !renamedRow[key] || renamedRow[key].length <= 0) {
        return;
      }
      if (renamedRow && renamedRowsAll && renamedRowsAll[key]) {
        renamedRow[key].forEach((elem) => {
          const index = renamedRowsAll[key].indexOf(elem, 0);
          if (index > -1) {
            renamedRowsAll[key].splice(index, 1);
          }
        });
      }
    });

    const newRenames = renamedRowsAll ? { ...this.state.renamedRows, ...renamedRowsAll } : {};
    this.setState(
      {
        renamedRows: removeNullValues(newRenames),
      },

      this.handleChange,
    );
  };

  componentDidMount() {
    const { selectedColumns, renamedRows, columns, isValid, overridesConfig } = this.props;
    this.setSelectedRow(selectedColumns || []);
    const oldRenamedRows = this.getInitialRenamedRows(columns);
    this.initializeRenamedRows(renamedRows || oldRenamedRows || {});
    const oldoverridesConfig = this.getInitialOverrideConfig(columns);
    this.setOverridesConfig(overridesConfig || oldoverridesConfig || {});
    if (isValid) {
      this.fetchTableData();
    }
  }

  fetched = false;

  static getDerivedStateFromProps(nextProps: Partial<TableMappingProviderProps>, currState: State) {
    if (!_.isEqual(currState.sourceConfig, nextProps.sourceConfig)) {
      return { ...currState, sourceConfig: nextProps.sourceConfig };
    }
    return null;
  }

  componentDidUpdate(prevProps: FixMe, prevState: FixMe) {
    const newColumns = !_.isEqual(prevProps.columns?.sort(), this.props.columns?.sort());
    const oldoverridesConfig = this.getInitialOverrideConfig(this.props.columns);
    const oldRenamedRows = this.getInitialRenamedRows(this.props.columns);
    if (
      (!this.fetched && this.props.isValid) ||
      (newColumns && this.props.isValid) ||
      (this.props.isValid && this.props.isValid !== prevProps.isValid)
    ) {
      this.fetchTableData(!newColumns || !prevProps?.columns?.length);
      this.setOverridesConfig(
        Object.keys(this.state.overridesConfig || {})?.length > 0
          ? this.state.overridesConfig
          : oldoverridesConfig,
      );
      this.initializeRenamedRows(
        Object.keys(this.state.renamedRows).length > 0 ? this.state.renamedRows : oldRenamedRows,
      );
    } else if (newColumns) {
      this.setSelectedRow([]);
      this.setConstantConfig([]);
      this.initializeRenamedRows(oldRenamedRows);

      this.setOverridesConfig(oldoverridesConfig);
    }
    if (
      JSON.stringify(this.props.selectedColumns || []) !==
      JSON.stringify(prevProps.selectedColumns || [])
    ) {
      this.setSelectedRow(this.props.selectedColumns || []);
    }
    if (
      this.state.selectedColumns?.length !== prevState.selectedColumns?.length ||
      this.state.overridesConfig !== prevState.overridesConfig
    ) {
      this.setJSON();
    }
  }

  fetchTableData = async (defaultColsFromProps = true) => {
    const {
      sourceConfig,
      isValid,
      columns,
      role,
      selectedColumns,
      sourceDataStore,
      renamedRows,
      overridesConfig,
      messagesStore,
      updateSchemaErrors,
    } = this.props;
    try {
      if (!isValid) {
        return;
      }
      this.setState({
        loading: true,
      });
      this.fetched = true;
      const previewOrError = await sourceDataStore!.getPreview(
        sourceConfig.accountId || '',
        role,
        sourceConfig,
      );
      const { rows } = getRespData(previewOrError);
      const tableData = generatePreview(rows, columns?.map((col) => ({ name: col })) || []);
      const oldoverridesConfig = this.getInitialOverrideConfig(columns);
      const oldRenamedRows = this.getInitialRenamedRows(columns);
      const newState: Pick<
        State,
        'tableData' | 'loading' | 'selectedColumns' | 'renamedRows' | 'overridesConfig'
      > = {
        tableData,
        renamedRows: renamedRows || oldRenamedRows || {},
        loading: false,
        selectedColumns: selectedColumns || columns || [],
        overridesConfig: overridesConfig || oldoverridesConfig || {},
      };
      if (!defaultColsFromProps || !columns || columns.length === 0) {
        newState.selectedColumns = columns || [];
        newState.renamedRows = oldRenamedRows;

        newState.overridesConfig = oldoverridesConfig;
      }
      this.setState(newState, () => {
        this.setJSON();
        this.handleChange();
      });
    } catch (err) {
      this.setState({
        loading: false,
      });
      const errDetails = getApiErrorMessageDetails(err, 'Could not fetch preview');
      updateSchemaErrors([fetchErrorAssets('preview', errDetails.message)]);
      messagesStore?.showErrorMessage(errDetails.messageWithFallback);
    }
  };

  setJSON = async () => {
    const { eventType } = this.props;
    const { tableData, selectedColumns, overridesConfig } = this.state;
    if (!tableData) {
      return;
    }
    const noTraitsFields = overrides.filter((col) => col !== 'email');
    let jsonData: { [i: string]: FixMe } = { type: eventType };
    const cols = Object.keys(tableData! || {});

    Object.keys(overridesConfig || {}).forEach((key: string) => {
      if (tableData && overridesConfig) {
        jsonData[key] = tableData[overridesConfig[key]];
      }
    });
    jsonData = cols.reduce((accum: FixMe, col) => {
      if (
        noTraitsFields.includes(col.toLowerCase()) &&
        selectedColumns.includes(col) &&
        !important_overrides.includes(col.toLowerCase())
      ) {
        accum[col.toLowerCase()] = tableData![col];
      }
      return accum;
    }, jsonData);
    jsonData = cols.reduce((accum: FixMe, col) => {
      if (!accum.traits) {
        accum.traits = {};
      }
      if (important_overrides.includes(col.toLowerCase()) && selectedColumns.includes(col)) {
        accum.traits[`${col}_dup`] = tableData![col];
        return accum;
      }
      if (!noTraitsFields.includes(col.toLowerCase()) && selectedColumns.includes(col)) {
        const key = col;
        accum.traits[key] = tableData![col];
      }
      return accum;
    }, jsonData);
    this.setState({
      jsonData: [jsonData],
    });
  };

  refreshColumns = (columns: IColumn[]) => {
    const newColumns = columns.map((col) => col.name);
    this.props.setColumns(newColumns);
  };

  refreshPreview = (rows: Record<string, unknown>[]) => {
    const tableData = generatePreview(
      rows,
      this.props.columns?.map((col) => ({ name: col })) || [],
    );

    this.setState(
      {
        tableData,
      },
      () => {
        this.setJSON();
      },
    );
  };

  render() {
    const { children, updateSchemaErrors } = this.props;
    const values: MappingState = {
      ...this.state,
      setConstantConfig: this.setConstantConfig,
      deleteConstantConfig: this.deleteConstantConfig,
      setSelectedRow: this.setSelectedRow,
      setEditingRow: this.setEditingRow,
      setRenamedRows: this.setRenamedRows,
      setSearchKeyword: this.setSearchKeyword,
      setOverridesConfig: this.setOverridesConfig,
      deleteFromRenamedRow: this.deleteFromRenamedRow,
      validateName: this.validateName,
      refreshColumns: this.refreshColumns,
      refreshPreview: this.refreshPreview,
      updateSchemaErrors,
    };
    return <MappingContext.Provider value={values}>{children}</MappingContext.Provider>;
  }
}

export default MappingTableProvider;
